import { createContext, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { SidebarProvider, SidebarTrigger, SidebarInset, useSidebar } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { useAppMode } from '@/hooks/useAppMode';
import { Button } from '@/components/ui/button';
import { MessageSquarePlus } from 'lucide-react';
import { navigateWithParams } from '@/lib/navigation-utils';
import { LEGACY_FEATURES_ENABLED } from '@/lib/features';

export const HeaderSlotContext = createContext(() => {});

// Inner layout component that can access sidebar context
function LayoutContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, isMobile } = useSidebar();
  const [headerSlot, setHeaderSlot] = useState(null);

  // Calculate margin based on sidebar state (only on desktop)
  // Use inline style for CSS variable since Tailwind arbitrary values can be tricky
  const marginLeft = !isMobile
    ? state === 'expanded'
      ? 'var(--sidebar-width)'
      : 'var(--sidebar-width-icon)'
    : '0';

  return (
    <HeaderSlotContext.Provider value={setHeaderSlot}>
      <SidebarInset
        className="h-svh overflow-x-hidden transition-[margin] duration-200 ease-linear"
        style={{ marginLeft }}
      >
        <header className="sticky top-0 z-50 flex h-12 shrink-0 items-center gap-2 border-b border-border bg-background px-3">
          <SidebarTrigger className="-ml-1" />
          {headerSlot}
          {LEGACY_FEATURES_ENABLED && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                // Use programmatic navigation with state to force React Router to treat it as new navigation
                // even when already on the same route
                navigateWithParams(navigate, '/', {
                  replace: false,
                  state: { resetChat: Date.now() }
                });
              }}
              className="ml-auto gap-2"
            >
              <MessageSquarePlus className="h-4 w-4" />
              New Chat
            </Button>
          )}
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto">
          <Outlet />
        </div>
      </SidebarInset>
    </HeaderSlotContext.Provider>
  );
}

export function Layout() {
  const { isIframe } = useAppMode();

  return (
    <SidebarProvider
      defaultOpen={!isIframe}
      className={isIframe ? 'h-full' : 'min-h-screen'}
    >
      <AppSidebar />
      <LayoutContent />
    </SidebarProvider>
  );
}
